import { useState } from "react";
import { ArrowUpRight, Plus, Upload, X } from "lucide-react";
import { readFileAsDataURL } from "../lib/api";

export default function IndentForm({ departments = [], items = [], onSubmit, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    department: departments[0]?.name || "",
    raised_by: "",
    raised_by_role: "Doctor",
    indent_number: `IND-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 8999)}`,
    issue_date: today,
    indent_date: today,
    notes: "",
    upload_name: "",
    upload_data: "",
  });
  const [lines, setLines] = useState([{ name: items[0]?.name || "", qty: 1, unit: items[0]?.base_unit || "", batch: "" }]);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    const data = await readFileAsDataURL(file);
    update("upload_name", file.name);
    update("upload_data", data);
  };

  const addLine = () => setLines((l) => [...l, { name: items[0]?.name || "", qty: 1, unit: items[0]?.base_unit || "", batch: "" }]);
  const updateLine = (i, k, v) => setLines((l) => l.map((row, idx) => {
    if (idx !== i) return row;
    const updated = { ...row, [k]: v };
    if (k === "name") {
      const item = items.find((x) => x.name === v);
      if (item) updated.unit = item.base_unit;
    }
    return updated;
  }));
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const unitOptions = (name) => {
    const item = items.find((x) => x.name === name);
    return item?.units?.map((u) => u.name) || [item?.base_unit || ""];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({
        ...form,
        items: lines.filter((l) => l.name).map((l) => ({ ...l, qty: Number(l.qty) })),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-grid">
        <label>Department
          <select data-testid="department-select" value={form.department} onChange={(e) => update("department", e.target.value)}>
            {departments.map((d) => <option key={d.id || d.name}>{d.name}</option>)}
          </select>
        </label>
        <label>Indent raised by<input data-testid="raised_by-input" value={form.raised_by} onChange={(e) => update("raised_by", e.target.value)} required /></label>
        <label>Role
          <select data-testid="raised_by_role-select" value={form.raised_by_role} onChange={(e) => update("raised_by_role", e.target.value)}>
            <option>Doctor</option><option>Nurse</option><option>Ward-in-charge</option>
          </select>
        </label>
        <label>Indent number<input data-testid="indent_number-input" value={form.indent_number} onChange={(e) => update("indent_number", e.target.value)} required /></label>
        <label>Indent date<input data-testid="indent_date-input" type="date" value={form.indent_date} onChange={(e) => update("indent_date", e.target.value)} /></label>
        <label>Issue date<input data-testid="issue_date-input" type="date" value={form.issue_date} onChange={(e) => update("issue_date", e.target.value)} /></label>
        <label className="full">Notes
          <textarea data-testid="notes-input" rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
        </label>
        <label className="full">Indent PDF
          <div className="file-input">
            <Upload size={16} />
            <span>{form.upload_name || "Attach PDF (stored inline)"}</span>
            <input data-testid="upload-input" type="file" accept="application/pdf" onChange={(e) => handleFile(e.target.files?.[0])} />
          </div>
        </label>
      </div>

      <div className="units-block">
        <div className="units-head">
          <div>
            <p className="eyebrow">ITEMS REQUESTED</p>
            <h4>What is the department asking for?</h4>
          </div>
          <button type="button" className="secondary sm" data-testid="add-line-button" onClick={addLine}><Plus size={14}/> Add line</button>
        </div>
        <div className="unit-rows">
          {lines.map((l, i) => (
            <div className="line-row" key={i}>
              <select data-testid={`line-name-${i}`} value={l.name} onChange={(e) => updateLine(i, "name", e.target.value)}>
                {items.map((it) => <option key={it.id || it.name}>{it.name}</option>)}
              </select>
              <input placeholder="Qty" data-testid={`line-qty-${i}`} type="number" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} />
              <select data-testid={`line-unit-${i}`} value={l.unit} onChange={(e) => updateLine(i, "unit", e.target.value)}>
                {unitOptions(l.name).map((u) => <option key={u}>{u}</option>)}
              </select>
              <input placeholder="Batch (optional)" data-testid={`line-batch-${i}`} value={l.batch} onChange={(e) => updateLine(i, "batch", e.target.value)} />
              <button type="button" className="icon-btn ghost" onClick={() => removeLine(i)}><X size={14}/></button>
            </div>
          ))}
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="secondary" onClick={onClose}>Cancel</button>
        <button className="primary" data-testid="form-submit-button" disabled={busy}>{busy ? "Saving…" : "Create indent"} <ArrowUpRight size={16}/></button>
      </div>
    </form>
  );
}
